import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * Bronson Header component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-header.html
 *
 * @param {React.ReactNode|React.ReactNode[]} [children] - Extend the Header with {@link ContextBar} or {@link SiteNav}.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {React.ReactNode} [logo] - Extend the Header with {@link Logo}.
 * @param {React.ReactNode} [notificationCenter] - Extend the Header with {@link NotificationCenter}.
 * @param {boolean} [staticHeader] - Force the Header to be static.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The Header component.
 * @constructor
 */
export function Header({ children, className, logo, notificationCenter, staticHeader, testId, ...otherProps }) {
  const classNameList = classNames(
    'c-header',
    {
      'c-header--static': staticHeader, // Convenience prop from Bronson variants.
    },
    className
  ).trim()

  function renderIfNotificationCenter() {
    if (notificationCenter) {
      return <div className="c-header__notification-center">{notificationCenter}</div>
    }
    return null
  }

  return (
    <header className="c-header__wrapper" data-testid={testId} {...otherProps}>
      <div className={classNameList}>
        <div className="c-header__content-wrapper">
          <div className="c-header__logo">{logo}</div>
          {React.Children.map(children, (child) => {
            if (!child) {
              return null
            }
            return child.props.isContextBar ? child : <div className="c-header__nav">{child}</div>
          })}
          {renderIfNotificationCenter()}
        </div>
      </div>
    </header>
  )
}

Header.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]), // Bronson template: 'bronson-context-bar' or 'bronson-site-nav'. Use {@link ContextBar} or {@link SiteNav}.
  className: PropTypes.string, // Bronson template: 'header-modifier'.
  logo: PropTypes.node, // Bronson template: 'bronson-logo'. Use {@link Logo}.
  notificationCenter: PropTypes.node, // Bronson template: 'bronson-notification-center'. Use {@link NotificationCenter}.
  testId: PropTypes.string, // // Added for the `[data-testid]` attribute.
  /* Convenience props. */
  staticHeader: PropTypes.bool, // Bronson template: 'header-modifier'. Convenience prop for `.c-header--static`.
}
